/* eslint-disable react-refresh/only-export-components -- formatTimeAgo is co-located with ActionRowItem as it is only used by this component and its history-mode callers */
import {
  memo,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import { CheckboxBase } from '@/lib/base/forms/checkbox';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { stripEntityTags } from '@/lib/markdown/entityRendering';
import { cx } from '@/utils/cx';

import type { ProposalListItem } from '../../lib/proposals/core/types';
import { NAV_SHORTCUT_LABELS } from './navShortcutLabels';
import { QuickApproveButton } from './ProposalListApproveButton';

/**
 * Format a date string as a relative time (e.g., "5 minutes ago").
 * Pure function kept outside component to avoid recreation on each render.
 */
export const formatTimeAgo = (dateString: string): string => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInMinutes = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60)
  );

  if (diffInMinutes < 1) {
    return 'Just now';
  }
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
  }

  return date.toLocaleDateString();
};

interface ActionRowItemBaseProps {
  action: ProposalListItem;
  isSelected: boolean;
  isCrmOnly: boolean;
  isNew: boolean;
  showShortcuts: boolean;
  isLastOfLastCategory: boolean;
  onMouseDown: (id: number) => void;
  onClick: (id: number) => void;
  getSourceIcon: (action: ProposalListItem) => ReactNode;
}

// History mode: uses resolved_at for timestamp and hides
// checkbox/unread-dot/quick-approve — the pending-only affordances are
// required whenever isHistory is false/omitted, so this is a discriminated
// union rather than a flat interface with optional fields. History rows show
// no per-row resolution badge: each history tab holds exactly one resolution
// (RESOLUTION_FOR_VIEW), so a badge would repeat the tab's own label.
export type ActionRowItemProps =
  | (ActionRowItemBaseProps & {
      isHistory: true;
      isChecked?: undefined;
      approvingId?: undefined;
      onToggleSelected?: undefined;
      onQuickApprove?: undefined;
    })
  | (ActionRowItemBaseProps & {
      isHistory?: false;
      isChecked: boolean;
      approvingId: number | null;
      /** shiftKey enables range selection from the caller's anchor row. */
      onToggleSelected: (id: number, shiftKey?: boolean) => void;
      onQuickApprove: (id: number) => void;
    });

export const ActionRowItem = memo(function ActionRowItem({
  action,
  isSelected,
  isChecked,
  isCrmOnly,
  isNew,
  showShortcuts,
  isLastOfLastCategory,
  approvingId,
  onMouseDown,
  onClick,
  onToggleSelected,
  onQuickApprove,
  getSourceIcon,
  isHistory = false,
}: ActionRowItemProps) {
  // Keep the current action in view: when this row becomes selected
  // (keyboard nav, approve/reject advancing to the next action, deep link),
  // scroll the minimal amount needed. block:'nearest' is a no-op when the
  // row is already fully visible, so click-selection never causes a jump.
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (isSelected) {
      rootRef.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [isSelected]);

  const handleMouseDown = useCallback(() => {
    onMouseDown(action.id);
  }, [onMouseDown, action.id]);

  const handleClick = useCallback(() => {
    onClick(action.id);
  }, [onClick, action.id]);

  const handleCheckboxMouseDown = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    // Shift+click is range selection — suppress the browser's shift-click
    // text-selection behavior, which starts on mousedown.
    if (e.shiftKey) {
      e.preventDefault();
    }
  }, []);

  const handleCheckboxClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onToggleSelected?.(action.id, e.shiftKey);
    },
    [onToggleSelected, action.id]
  );

  const handleQuickApproveClick = useCallback(() => {
    onQuickApprove?.(action.id);
  }, [onQuickApprove, action.id]);

  const timestamp = formatTimeAgo(
    isHistory ? (action.resolved_at ?? action.created_at) : action.created_at
  );

  return (
    <div
      ref={rootRef}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      className={cx(
        'relative flex items-start gap-[8px] border-t border-[var(--color-border-secondary)] pl-[10px] pr-[16px] py-[8px] cursor-pointer transition-colors',
        isLastOfLastCategory && 'border-b',
        isSelected ? 'bg-bg-secondary' : 'bg-bg-primary hover:bg-bg-secondary',
        isNew && 'proposal-enter'
      )}
    >
      {/* Navigation shortcut overlay */}
      {showShortcuts && isSelected && (
        <div className='absolute right-3 top-1/2 -translate-y-1/2 z-10 flex flex-col gap-0.5 items-center'>
          <span className='text-[10px] font-medium text-fg-tertiary bg-bg-tertiary px-1.5 py-0.5 rounded'>
            {NAV_SHORTCUT_LABELS.up}
          </span>
          <span className='text-[10px] font-medium text-fg-tertiary bg-bg-tertiary px-1.5 py-0.5 rounded'>
            {NAV_SHORTCUT_LABELS.down}
          </span>
        </div>
      )}

      {/* Gutter: checkbox + avatar, centered against the 20px title line so
          the description column aligns under the title (no zigzag left edge). */}
      {!isHistory && (
        <button
          type='button'
          className='flex-shrink-0 flex h-[20px] items-center'
          aria-label={isChecked ? 'Deselect action' : 'Select action'}
          aria-pressed={isChecked ?? false}
          onMouseDown={handleCheckboxMouseDown}
          onClick={handleCheckboxClick}
        >
          <CheckboxBase isSelected={isChecked ?? false} />
        </button>
      )}
      <div
        className={cx(
          'flex-shrink-0 flex h-[20px] items-center',
          isNew && 'proposal-icon'
        )}
      >
        {getSourceIcon(action)}
      </div>

      {/* Content column: title line (dot | title | timestamp | quick approve)
          with the description directly beneath the title. */}
      <div className='min-w-0 flex-1'>
        {/* h-[20px] is load-bearing: the gutter above pins the checkbox and
            source icon to a 20px box to match the title's line height, so this
            row must stay 20px too. Left to size itself it takes the height of
            its tallest child — the 22px quick-approve button — and items-center
            then pushes the title and timestamp down half the difference, out of
            line with the gutter. Controls taller than the line overflow it
            symmetrically instead, which is what the gutter icons already do. */}
        <div className='flex h-[20px] items-center gap-[8px]'>
          {!isHistory && action.read_at === null && (
            <span
              aria-label='Unread'
              data-testid='unread-dot'
              className='flex-shrink-0 w-[8px] h-[8px] rounded-full bg-[var(--color-brand-600)]'
            />
          )}
          <h3 className='font-semibold text-[14px] leading-[20px] text-[var(--color-text-primary)] truncate min-w-0 flex-1'>
            {action.title}
          </h3>
          <span
            className={cx(
              'flex-shrink-0 font-normal text-[11px] leading-[20px] whitespace-nowrap text-fg-quaternary',
              // The keyboard-shortcut chips overlay this row's right edge —
              // hide (not unmount) the timestamp so the chips don't garble it.
              showShortcuts && isSelected && 'invisible'
            )}
          >
            {timestamp}
          </span>
          {!isHistory && isCrmOnly && (
            <QuickApproveButton
              onClick={handleQuickApproveClick}
              ariaLabel={`Quick approve ${action.title}`}
              disabled={approvingId !== null}
            />
          )}
        </div>
        {action.description && (
          <div className='mt-[2px] font-normal text-[13px] text-secondary line-clamp-2'>
            {/* [&_p]:leading-[18px] restores this row's original description
                line-height: compact MarkdownContent's <p> hard-codes leading-6
                (24px) to seat inline MentionBadge pills, but stripEntityTags
                removes all mentions here, so the badge allowance is dead weight
                that would grow each clamped row ~12px past the intended layout. */}
            <MarkdownContent
              content={stripEntityTags(action.description)}
              size='compact'
              className='[&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_p]:leading-[18px]'
            />
          </div>
        )}
      </div>
    </div>
  );
});
